import React, { useState, useEffect } from 'react';
import { Box, Select, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { useClientContext } from '@/common/application/contexts/ClientContext';

const ClientSelector: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    clients,
    clientsLoading,
    selectedClient,
    setSelectedGlobalUserMappingID,
    shouldOpenClientSelector,
    setShouldOpenClientSelector,
  } = useClientContext();

  const [selectOpen, setSelectOpen] = useState(false);
  const isClientActive = (client: any) => client && client.isActive !== false && client.isActive !== 0;
  // MappingID is unique across client DBs; ClientPersonID is not (can collide, e.g. both = 1).
  const selectedMappingID = selectedClient?.globalUserMappingID ?? '';

  const handleClientChange = (event: any) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedGlobalUserMappingID(null);
    } else {
      setSelectedGlobalUserMappingID(Number(value));
    }
  };

  const getClientInitials = (clientName: string) => {
    return clientName
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // MarqueeText component for scrolling text effect
  const MarqueeText: React.FC<{ text: string; maxWidth?: number }> = ({ text, maxWidth = 100 }) => {
    const [shouldMarquee, setShouldMarquee] = useState(false);

    useEffect(() => {
      // Check if text width exceeds maxWidth
      const textElement = document.createElement('span');
      textElement.style.visibility = 'hidden';
      textElement.style.position = 'absolute';
      textElement.style.fontSize = '14px';
      textElement.style.fontFamily = 'Inter, sans-serif';
      textElement.textContent = text;
      document.body.appendChild(textElement);

      const textWidth = textElement.offsetWidth;
      document.body.removeChild(textElement);

      setShouldMarquee(textWidth > maxWidth);
    }, [text, maxWidth]);

    if (shouldMarquee) {
      return (
        <Box
          sx={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '20px',
              height: '100%',
              background: 'linear-gradient(to right, transparent, #2D3748)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-block',
              animation: 'marquee 15s linear infinite',
              '@keyframes marquee': {
                '0%': { transform: 'translateX(0%)' },
                '20%': { transform: 'translateX(0%)' },
                '80%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(-100%)' },
              },
            }}
          >
            {text}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {text}
      </Box>
    );
  };

  useEffect(() => {
    if (shouldOpenClientSelector) {
      setSelectOpen(true);
      setShouldOpenClientSelector(false);
    }
  }, [shouldOpenClientSelector, setShouldOpenClientSelector]);

  return (
    <Select
      value={selectedMappingID}
      onChange={handleClientChange}
      disabled={clientsLoading}
      displayEmpty
      open={selectOpen}
      onOpen={() => setSelectOpen(true)}
      onClose={() => setSelectOpen(false)}
      sx={{
        backgroundColor: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '8px',
        color: 'white',
        width: isMobile ? '150px' : '320px',
        minWidth: isMobile ? '120px' : '320px',
        maxWidth: isMobile ? '180px' : '320px',
        height: '50px',
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& .MuiSelect-icon': {
          color: 'white',
        },
        '& .MuiSelect-select': {
          padding: '8px 12px',
          fontSize: '14px',
          height: '40px!important',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            backgroundColor: '#2D3748',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            marginTop: '4px',
            maxHeight: isMobile ? '300px' : '400px',
            maxWidth: isMobile ? '280px' : '420px',
            '& .MuiMenuItem-root': {
              color: 'white',
              borderRadius: '4px',
              margin: '2px 8px',
              minHeight: '48px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            },
          },
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        disableScrollLock: true,
      }}
    >
      <MenuItem value="" disabled>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            S
          </Box>
          <Box sx={{ width: '100%' }}>
            <MarqueeText text={clientsLoading ? 'Loading...' : 'Select Client'} maxWidth={isMobile ? 80 : 200} />
          </Box>
        </Box>
      </MenuItem>
      {clients
        .filter((client: any) => client && client.globalUserMappingID != null && isClientActive(client))
        .map((client: any) => (
          <MenuItem key={`client-${client.globalUserMappingID}`} value={client.globalUserMappingID}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'none', width: '100%' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                {getClientInitials(client.displayName || client.name)}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, width: '100%' }}>
                <Box sx={{ color: 'white', fontWeight: 500, fontSize: '14px', width: '100%' }}>
                  <MarqueeText text={client.displayName || client.name} maxWidth={isMobile ? 120 : 250} />
                </Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', width: '100%' }}>
                  <MarqueeText text={client.contractorName} maxWidth={isMobile ? 120 : 250} />
                </Box>
              </Box>
            </Box>
          </MenuItem>
        ))}
    </Select>
  );
};

export default ClientSelector;
